import {useI18n} from '@udemy/i18n';
import {
    ConsumerSubscriptionPlan,
    ConsumerSubscriptionValuePlan,
    SubscriptionPlanContext,
    useConsumerSubscriptionValuePlan,
} from '@udemy/subscription-graphql';

const STARTER_PLAN_COURSE_COUNT = 12000;
const PERSONAL_PLAN_COURSE_COUNT = 27000;

export const calculateNumCourses = (
    planContext:
        | SubscriptionPlanContext<ConsumerSubscriptionValuePlan>
        | SubscriptionPlanContext<ConsumerSubscriptionPlan>,
): number => {
    // use starter plan course count as fallback since smaller amount
    let fallbackCount = STARTER_PLAN_COURSE_COUNT;

    if (planContext && 'productType' in planContext) {
        if (planContext.productType === 'CONSUMERSUBSCRIPTION_VALUE_PLAN') {
            fallbackCount = STARTER_PLAN_COURSE_COUNT;
        } else if (planContext.productType === 'CONSUMERSUBSCRIPTION') {
            fallbackCount = PERSONAL_PLAN_COURSE_COUNT;
        }
    }

    if (!planContext || !('contentCollections' in planContext)) {
        return fallbackCount;
    }

    return (
        (planContext.contentCollections ?? [])
            .map((value) => value?.stats?.numberOfCourses)
            .reduce((sum, current) => (sum ?? 0) + (current ?? 0), 0) ?? fallbackCount
    );
};

export const useValuePlanExperimentLogic = () => {
    const {gettext} = useI18n();
    const valuePlanContext = useConsumerSubscriptionValuePlan();

    const CTA_TEXT = {
        CURRENT_PLAN: gettext('Your current plan'),
        NOT_AVAILABLE: gettext('Not available'),
        GET_STARTED: gettext('Get started'),
        UPGRADE: gettext('Upgrade'),
        RECOMMENDED: gettext('Recommended'),
    };

    // Determine subscriber status for CTA logic
    const isValuePlanSubscriber =
        valuePlanContext && 'isSubscriberToSpecificPlan' in valuePlanContext
            ? valuePlanContext.isSubscriberToSpecificPlan
            : false;

    const getCtaText = (planType: 'value' | 'personal', isPersonalPlanSubscriber: boolean) => {
        if (planType === 'value') {
            if (isValuePlanSubscriber) {
                return CTA_TEXT.CURRENT_PLAN;
            } else if (isPersonalPlanSubscriber) {
                return CTA_TEXT.NOT_AVAILABLE;
            }
            return CTA_TEXT.GET_STARTED;
        } else {
            if (isPersonalPlanSubscriber) {
                return CTA_TEXT.CURRENT_PLAN;
            } else if (isValuePlanSubscriber) {
                return CTA_TEXT.UPGRADE;
            }
            return CTA_TEXT.GET_STARTED;
        }
    };

    return {
        CTA_TEXT,
        isValuePlanSubscriber,
        getCtaText,
        valuePlanContext,
    };
};
